import axios, { CancelTokenSource } from 'axios';
import { useCallback, useEffect, useRef, useState } from 'react';

import {
  createCampaignFilter,
  getCampaignFilters,
  getCampaignFiltersByNextUrl,
  removeCampaignFilter,
  updateCampaignFilter,
} from './api';
import {
  CampaignFilter,
  CampaignFiltersResponse,
  CampaignFilterTypeValues,
  CreateCampaignFilter,
  UpdateCampaignFilter,
} from './domain';

type Options = {
  search?: string;
  filter?: CampaignFilterTypeValues;
  shouldFetch?: boolean;
};

export const useCampaignFilters = (
  campaignId: number,
  options: Options = {
    search: undefined,
    filter: undefined,
    shouldFetch: true,
  },
) => {
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<boolean>(false);
  const [list, setList] = useState<CampaignFilter[]>([]);
  const [hasMore, setHasMore] = useState<boolean>(false);
  const [nextUrl, setNextUrl] = useState<string | null>(null);
  const axiosCancelRef = useRef<CancelTokenSource>(axios.CancelToken.source());
  const observer = useRef<IntersectionObserver | undefined>(undefined);
  const search = options.search;
  const filter = options.filter;
  const shouldFetch = options.shouldFetch ?? true;

  const getNextPage = useCallback(async () => {
    if (nextUrl !== null) {
      setLoading(true);
      setError(false);

      let resp: CampaignFiltersResponse | undefined;
      try {
        axiosCancelRef.current = axios.CancelToken.source();
        resp = await getCampaignFiltersByNextUrl(nextUrl, axiosCancelRef.current);
      } catch (e) {
        setError(true);
        setLoading(false);
        return;
      }

      // Returns undefined if request is canceled
      if (resp === undefined) return;

      setList((prev) => [...prev, ...resp!.filters]);
      setHasMore(resp.nextPageUrl !== null);
      setNextUrl(resp.nextPageUrl);
      setLoading(false);
    }
  }, [nextUrl]);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(false);

    let resp: CampaignFiltersResponse | undefined;
    try {
      axiosCancelRef.current = axios.CancelToken.source();
      resp = await getCampaignFilters(campaignId, search, filter, axiosCancelRef.current);
    } catch (e) {
      setError(true);
      setLoading(false);
      return;
    }

    // Returns undefined if request is canceled
    if (resp === undefined) return;

    setList(resp.filters);
    setHasMore(resp.nextPageUrl !== null);
    setNextUrl(resp.nextPageUrl);
    setLoading(false);
  }, [search, filter, campaignId]);

  /** Ref watches for element view intersection and loads more results. Note: Should only be assigned to last element in
   * a list
   * */
  const intersectionObserverRef = useCallback(
    (node: any) => {
      if (loading) return;
      if (observer.current) observer.current.disconnect();
      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMore) {
          getNextPage();
        }
      });
      if (node) observer.current.observe(node);
    },
    [loading, hasMore, getNextPage],
  );

  const create = useCallback(
    async (data: CreateCampaignFilter) => {
      try {
        await createCampaignFilter(campaignId, data);
      } catch (e) {
        // Do nothing
        return Promise.reject(e);
      }

      await reload();
    },
    [campaignId, reload],
  );

  const update = useCallback(
    async (filterId: number, data: UpdateCampaignFilter) => {
      try {
        await updateCampaignFilter(campaignId, filterId, data);
      } catch (e) {
        // Do nothing
        return Promise.reject(e);
      }

      await reload();
    },
    [campaignId, reload],
  );

  const remove = useCallback(
    async (filterId: number) => {
      try {
        await removeCampaignFilter(campaignId, filterId);
      } catch (e) {
        // Do nothing
        return Promise.reject(e);
      }

      await reload();
    },
    [campaignId, reload],
  );

  useEffect(() => {
    setList([]);
  }, [search, filter, campaignId, shouldFetch]);

  useEffect(() => {
    const load = async (search?: string, filter?: CampaignFilterTypeValues, campaignId?: number) => {
      setLoading(true);
      setError(false);

      let resp: CampaignFiltersResponse | undefined;
      try {
        axiosCancelRef.current = axios.CancelToken.source();
        resp = await getCampaignFilters(campaignId, search, filter, axiosCancelRef.current);
      } catch (e) {
        setError(true);
        setLoading(false);
        return;
      }

      // Returns undefined if request is canceled
      if (resp === undefined) return;

      setList((prev) => [...prev, ...resp!.filters]);
      setHasMore(resp.nextPageUrl !== null);
      setNextUrl(resp.nextPageUrl);
      setLoading(false);
    };

    if (shouldFetch) {
      load(search, filter, campaignId);
    }

    return () => {
      // Cancel request if it has already been executed
      axiosCancelRef.current.cancel();
    };
  }, [search, filter, campaignId, shouldFetch]);

  return { loading, error, list, hasMore, intersectionObserverRef, create, remove, update };
};

export default useCampaignFilters;
